/**
 * How one breakdown line is laid out in the panel (E9 §C6 renderer, revised 2026-08-20).
 *
 * ## Why this replaced a table
 *
 * The first version drew a real `<table>` with one column per declared field, and gave it its own
 * horizontal scroller so a wide breakdown could not push the page sideways. That was the wrong trade.
 * `PickRecorded` reached SIX columns once PW-09 added Bin and Serial, and six columns do not fit 364px —
 * so the scroller stopped being a safety net and became the everyday experience: a warehouse supervisor
 * dragging a scrollbar to find out which bin a pick came from. Owner, on seeing it: *"i dont want users to
 * scroll."*
 *
 * A list wraps. A table scrolls. At this width the list wins, and it keeps winning when a seventh column
 * is declared.
 *
 * ## The plan, and why it is content-driven
 *
 * Nothing here reads a payload key or an event type — the client still never learns either (§C1). It reads
 * the finished strings the server sent and the headings it declared, exactly as the alignment rule already
 * did. That is what lets one renderer serve every event, including ones added after this file.
 */

/** Longest value a column can hold before it is treated as prose rather than a fact. */
const PROSE_MIN_LENGTH = 16;

const NUMERIC_CELL = /^[\d.,\s+-]+$/;

function isNumeric(value: string | null | undefined): boolean {
  return typeof value === 'string' && value.trim() !== '' && NUMERIC_CELL.test(value.trim());
}

function isPresent(value: string | null | undefined): value is string {
  return typeof value === 'string' && value.trim() !== '';
}

export interface LineLayout {
  /** Column rendered as the line's identity — monospace, first. Always 0 when there is any column. */
  codeIndex: number;
  /** Column rendered muted on its own second line, or `null` when no column is prose. */
  descriptionIndex: number | null;
  /** Columns rendered as `LABEL value` facts, in declared order. */
  factIndexes: number[];
  /** Columns dropped because every row left them empty. */
  emptyIndexes: number[];
}

/** The headings that carry a scanned identity — a serial or a lot, or the combined column a posting
 *  block declares (one posting mixes lot, serial and untracked lines, so its heading covers both). */
const IDENTITY_HEADINGS = new Set(['serial', 'lot', 'lot / serial']);

function isIdentityColumn(columns: readonly string[] | undefined, index: number): boolean {
  if (!columns) return false;
  return IDENTITY_HEADINGS.has(String(columns[index] ?? '').trim().toLowerCase());
}

/**
 * Decide, once per block, what each column is for.
 *
 * @param columnCount how many columns the block has
 * @param rows the finished cell strings, as the server sent them
 * @param columns the declared headings, when the caller has them — enables the identity guard (rule 3a)
 *
 * Rules, in order:
 *
 * 1. **A column nobody filled is dropped.** Serial is empty on every line of an untracked pick, and a
 *    column of em-dashes spends width to say nothing. This also means the same event renders narrower on
 *    the records that need less, which is the right way round.
 * 2. **Column 0 is the identity.** The long-standing positional convention — item code, bin code — and the
 *    only structural assumption made anywhere in the renderer.
 * 3. **A long, non-numeric column 1 is prose** and moves to its own muted line. `PickRecorded` puts a
 *    40-character item description there; `PutAwayRecorded` puts a QUANTITY there, which must NOT be
 *    demoted — hence both guards, not just the length one.
 *    **3a — an IDENTITY column is never prose.** `LOT-20260810 · exp 10 Feb 2027` is 30 characters of
 *    non-numeric text, which would sail past the length test and demote the whole block out of its
 *    bin-line treatment — turning a scanned identity into a muted sentence. Identity is identity.
 * 4. **Everything else is a fact**, rendered with its declared heading. The heading is what stops
 *    `A-12 · 50 · 45` being unreadable on a `PicklistAdjusted` row, where 50 and 45 are a from/to pair the
 *    client is deliberately not allowed to understand.
 */
export function planLineLayout(
  columnCount: number,
  rows: readonly (string | null)[][],
  columns?: readonly string[]
): LineLayout {
  const emptyIndexes: number[] = [];
  const populated: number[] = [];

  for (let index = 0; index < columnCount; index += 1) {
    const hasValue = rows.some((row) => isPresent(row?.[index]));
    (hasValue ? populated : emptyIndexes).push(index);
  }

  if (populated.length === 0) {
    return { codeIndex: 0, descriptionIndex: null, factIndexes: [], emptyIndexes };
  }

  const [codeIndex, ...rest] = populated;

  // Prose test runs on the SECOND populated column, not literally column 1 — otherwise dropping an empty
  // column would shift what counts as the description and change the layout for unrelated reasons.
  const candidate = rest[0];
  const isProse =
    candidate !== undefined &&
    !isIdentityColumn(columns, candidate) &&
    rows.some((row) => {
      const value = row?.[candidate];
      return isPresent(value) && !isNumeric(value) && value.trim().length >= PROSE_MIN_LENGTH;
    });

  return {
    codeIndex,
    descriptionIndex: isProse ? candidate : null,
    factIndexes: isProse ? rest.slice(1) : rest,
    emptyIndexes,
  };
}

/**
 * A fact as it should be drawn: the value, plus how to introduce it.
 *
 * `iconKey` is a KEY, never a component — this module stays free of React so it can actually be tested
 * (there is no jsdom in this repo, so anything importing a component is untestable by construction).
 */
export interface RenderedFact {
  /** The declared heading. Always kept: it is the tooltip and the accessible name even when an icon shows. */
  label: string;
  value: string;
  /** Lucide icon to draw INSTEAD of the label text, or null to draw the label text. */
  iconKey: FactIconKey | null;
  /**
   * True when the visible label should be dropped entirely — the value stands alone (`4 GALLON`, never
   * `QTY / UNIT 4 GALLON`). The label still survives in the tooltip and the accessible name.
   *
   * ⚠️ Decided HERE, at build/merge time, not in the component. The first shipped version exported a
   * `labelIsRedundant(label)` helper that nothing imported — and even wired up it would have missed the
   * main case, because the merged label `Qty / Unit` is in neither vocabulary set. A flag computed while
   * the sets are at hand cannot drift like that, and vitest can actually assert it.
   */
  hideLabel: boolean;
  /**
   * `'in'` / `'out'` on THE QUANTITY FACT ONLY, when the block declares a stock direction (§6) — the
   * component draws `↑ 2 PIECE` in success / `↓ 45 KG` in destructive from this. Null on every other
   * fact: a bin code never tints, and a `From`/`To` adjustment pair keeps its neutral arrows.
   */
  direction: 'in' | 'out' | null;
  /**
   * PT-7 A12 — true when the value is long enough to run past its row: a fact that DRAWS its label text (no
   * icon, not hidden), carries no direction, and holds at least `PROSE_MIN_LENGTH` characters — PT-2's
   * "For MRS" cell in its "{mrsNumber} — {remarks} · {endUse}" form is the case that needed it. The component
   * then lets the value wrap inside the row and clamps it, with the whole "{label}: {value}" in a native
   * title. A bare MRS number, a bin, a serial, a quantity and a time stay one nowrap span, as before.
   *
   * Decided here, like `hideLabel`, so vitest can assert it: the 364px dock feed is `overflow-x-hidden`, and
   * a nowrap fact wider than a row is cut at the dock's edge with no way to read the rest.
   */
  wraps: boolean;
}

export type FactIconKey = 'bin' | 'serial' | 'lot' | 'from' | 'to';

/**
 * ⚠️ DISPLAY-ONLY ENHANCEMENT, AND DELIBERATELY NOT A REGISTRY.
 *
 * `kindMap` warns against a second table needing lockstep with the server's projection map, and this is
 * not one: it is keyed on the DECLARED HEADING (display text the server already chose to show a person),
 * every entry is optional, and **anything unrecognised falls back to the heading text** — which is exactly
 * what shipped before icons existed. So a module that declares a new column, or renames one, degrades to
 * the old rendering rather than losing information. Nothing here reads a payload key or an event type.
 */
const ICON_BY_HEADING: Record<string, FactIconKey> = {
  bin: 'bin',
  location: 'bin',
  serial: 'serial',
  // A lot is NOT a serial: it draws the tag glyph, not the barcode (design §7). The combined heading a
  // posting block declares maps to the tag too — one line can carry either identity, and the tag is the
  // honest glyph for the mixed case.
  lot: 'lot',
  'lot / serial': 'lot',
  from: 'from',
  to: 'to',
};

/**
 * Headings whose value speaks for itself, so the label is noise.
 *
 * A bare `4` beside a bin is read as a quantity by anyone who has seen a picking screen. The label is
 * still carried for the tooltip and the accessible name — it is hidden, not discarded.
 */
const SELF_EVIDENT = new Set(['qty', 'quantity', 'unit', 'uom']);

/**
 * Headings that belong to the value BEFORE them rather than standing alone.
 *
 * `Qty 4` and `Unit GALLON` are one fact a person reads as "4 GALLON"; splitting them costs width and
 * reads worse. Merging happens here rather than server-side because the map declares them as separate
 * columns and #492's unit-safety rules depend on that separation staying intact in the payload.
 */
const MERGES_WITH_PREVIOUS = new Set(['unit', 'uom']);

/**
 * The quantity headings a declared stock direction attaches to. Deliberately NOT the whole SELF_EVIDENT
 * set: `unit`/`uom` merge into their quantity rather than standing alone, and a direction on a non-quantity
 * fact would paint a bin code green.
 */
const QUANTITY_HEADINGS = new Set(['qty', 'quantity']);

/**
 * Narrow the wire token to the two directions this module understands. Anything else — a typo, a future
 * third token — renders neutral rather than throwing; the render path is the worst place to discover one.
 */
export function narrowDirection(token: string | null | undefined): 'in' | 'out' | null {
  return token === 'in' || token === 'out' ? token : null;
}

/**
 * Turn one row into the facts to draw, in declared order, with units folded into their quantity.
 *
 * @param direction the block's declared stock direction, already narrowed — lands on the QUANTITY fact
 *   only (the merged `Qty / Unit` keeps it through the merge).
 */
export function buildFacts(
  columns: readonly string[],
  row: readonly (string | null)[],
  layout: LineLayout,
  direction: 'in' | 'out' | null = null
): RenderedFact[] {
  const facts: RenderedFact[] = [];

  for (const index of layout.factIndexes) {
    const value = row[index];
    if (!isPresent(value)) continue;

    const label = columns[index] ?? '';
    const key = label.trim().toLowerCase();

    if (MERGES_WITH_PREVIOUS.has(key) && facts.length > 0) {
      const previous = facts[facts.length - 1];
      facts[facts.length - 1] = {
        ...previous,
        value: `${previous.value} ${value.trim()}`,
        label: `${previous.label} / ${label}`,
        // The merge PRESERVES the hide decision. Qty is self-evident and Unit merges into it, so the
        // combined `Qty / Unit` stays hidden — which no label-text lookup could decide, because the
        // merged label is in neither vocabulary set. Same for the direction: `↓ 45` + `KG` must stay
        // one directional fact, not a directional number beside a neutral unit.
        hideLabel: previous.hideLabel,
        direction: previous.direction,
        // Kept through the merge, like the hide decision: a unit folding into its quantity never makes it prose.
        wraps: previous.wraps,
      };
      continue;
    }

    const iconKey = SELF_EVIDENT.has(key) ? null : (ICON_BY_HEADING[key] ?? null);
    // Only the SELF_EVIDENT set hides. An unknown heading — a future `Reason` or `Batch` — keeps its visible
    // label: a bare mystery value is exactly the failure the label exists to prevent.
    const hideLabel = SELF_EVIDENT.has(key);
    const factDirection = QUANTITY_HEADINGS.has(key) ? direction : null;
    const text = value.trim();
    facts.push({
      label,
      value: text,
      iconKey,
      hideLabel,
      direction: factDirection,
      wraps: iconKey === null && !hideLabel && factDirection === null && text.length >= PROSE_MIN_LENGTH,
    });
  }

  return facts;
}

// ─── Identity chips and the bin-grouped view (§5, 86d43a79d) ─────────────────────────────────────

/** One scanned identity, ready to draw as a chip. `exp` is the lot's expiry, already formatted by the
 *  server and split off the ` · exp ` delimiter it composes with — absent for most identities. */
export interface LineChip {
  text: string;
  exp: string | null;
  /** `serial` draws the scan-barcode glyph; `lot` the tag. A combined `Lot / Serial` column draws the
   *  tag — the honest glyph when one column can carry either identity. */
  iconKey: 'serial' | 'lot';
  /** The chip's own time, short (`10:29`), when the block declares one (`Picked at`). Owner 2026-10-08: a
   *  serial line lists its serials, each with the time it was picked, so the time rides on the chip. */
  at?: string;
  /** `Picked at 10:29:32 AM` — the full value the short time drops, for the chip's native title. */
  atTitle?: string;
}

/** The delimiter the server composes a lot expiry with. Split here, at the last moment — a seventh
 *  column would spend width on a field present on 16 of 158 lot rows. */
const EXP_DELIMITER = ' · exp ';

function chipKindFor(heading: string): 'serial' | 'lot' {
  return heading === 'serial' ? 'serial' : 'lot';
}

function toChip(value: string, heading: string): LineChip {
  const at = value.indexOf(EXP_DELIMITER);
  return at === -1
    ? { text: value, exp: null, iconKey: chipKindFor(heading) }
    : {
        text: value.slice(0, at),
        exp: value.slice(at + EXP_DELIMITER.length),
        iconKey: chipKindFor(heading),
      };
}

/** One line of the finished view. `inline` = bin-mode: the code shares a line with its facts
 *  (`📍 G-01-A-1  ↑ 2 PIECE`) instead of standing on its own line above them. */
export interface LinesViewLine {
  code: string | null;
  description: string | null;
  facts: RenderedFact[];
  chips: LineChip[];
  inline: boolean;
}

export interface LinesView {
  lines: LinesViewLine[];
  /** True when serialized/lot put-away rows were collapsed to one line per bin. */
  grouped: boolean;
}

const BIN_CODE_HEADINGS = new Set(['bin', 'location']);
const UNIT_HEADINGS = new Set(['unit', 'uom']);

function headingAt(columns: readonly string[], index: number): string {
  return String(columns[index] ?? '').trim().toLowerCase();
}

function parseQuantity(value: string | null | undefined): number | null {
  if (!isPresent(value)) return null;
  const n = Number(value.trim().replace(/,/g, ''));
  return Number.isFinite(n) ? n : null;
}

function formatQuantity(n: number): string {
  return n % 1 === 0 ? n.toLocaleString('en-US') : n.toFixed(2);
}

/**
 * The single entry point the component renders from (§3 of 86d43a79d). Everything the block decides is
 * decided HERE, where vitest can reach it — the component only draws.
 *
 * Serialized put-away is one payload element PER UNIT (Quantity=1, one serial each), so a 2-unit
 * put-away would draw two identical `G-01-A-1 | 1` lines. When the block is bin-mode — identity column
 * is Bin/Location, no prose column — and carries a Serial/Lot column, rows collapse to ONE LINE PER BIN:
 * quantity summed, the scanned identities as chips beneath. Three guards, all required:
 *
 * - **Any unparsable quantity aborts the grouping** for the whole block (ungrouped truth beats a wrong
 *   total).
 * - The payload is NEVER pre-grouped server-side — it is stored verbatim for audit parity, so the
 *   per-unit facts survive; this collapse is presentation only.
 * - Lines outside bin-mode (the posting blocks, with their item code + description) keep one line per
 *   element and render their identity as a chip per line (§3a) — the icon decided PER LINE (§3b),
 *   because one posting mixes lot, serial and untracked lines.
 */
export function planLinesView(
  columns: readonly string[],
  rows: readonly (string | null)[][],
  direction: 'in' | 'out' | null = null
): LinesView {
  const columnCount = Math.max(columns.length, ...rows.map((row) => row.length), 1);
  const layout = planLineLayout(columnCount, rows, columns);

  // ⚠️ The POPULATED identity column, not the first identity heading. Put-away declares SEPARATE
  // `Serial` and `Lot` columns and one is always empty (0 of 1,458 rows carry both) — looking up the
  // first matching heading would find the empty Serial column on every lot block and drop its chips.
  const idIdx = layout.factIndexes.find((i) => isIdentityColumn(columns, i)) ?? -1;

  // §3c — the identity column never doubles as a fact; the chip is its rendering.
  const factLayout: LineLayout =
    idIdx === -1 ? layout : { ...layout, factIndexes: layout.factIndexes.filter((i) => i !== idIdx) };

  const binMode =
    layout.descriptionIndex === null && BIN_CODE_HEADINGS.has(headingAt(columns, layout.codeIndex));

  if (binMode && idIdx !== -1) {
    const grouped = groupByBin(columns, rows, layout, idIdx, direction);
    if (grouped) return { lines: grouped, grouped: true };
  }

  if (idIdx !== -1) {
    const merged = groupByIdentity(columns, rows, layout, factLayout, idIdx, direction, binMode);
    if (merged) return merged;
  }

  const lines = rows.map((row): LinesViewLine => {
    const idValue = idIdx === -1 ? null : row[idIdx];
    return {
      code: row[layout.codeIndex] ?? null,
      description: layout.descriptionIndex === null ? null : (row[layout.descriptionIndex] ?? null),
      facts: buildFacts(columns, row, factLayout, direction),
      chips: isPresent(idValue) ? [toChip(idValue.trim(), headingAt(columns, idIdx))] : [],
      inline: binMode,
    };
  });

  return { lines, grouped: false };
}

/** Columns that add up when lines merge: a quantity, and an adjustment's From / To pair. */
const SUMMED_HEADINGS = new Set(['qty', 'quantity', 'from', 'to']);

/** A column holding the instant a line happened (`Picked at`): it rides on that line's chip. */
function isTimeHeading(heading: string): boolean {
  return heading.endsWith(' at');
}

/** `10:29:32 AM` → `10:29`. The full value stays in the chip's title, so nothing is lost. */
const CLOCK = /^(\d{1,2}:\d{2})(?::\d{2})?\s*(?:AM|PM)?$/i;

function shortTime(value: string): string {
  const match = CLOCK.exec(value.trim());
  return match ? match[1] : value.trim();
}

/**
 * Owner 2026-10-08 (PL-202610-00029's walk): a serial line is drawn per BIN, not per serial — lines that differ
 * only in their serial (and so in their quantity and time) merge into one, the quantities summed and the serials
 * listed as chips, each carrying the time it was picked. Before this, every serial of a pick or an adjustment was
 * its own line repeating the item, bin and MRS — 95 lines for one 95-serial pick.
 *
 * Presentation only, like §5's bin grouping: the server still sends one line per element. What merges:
 *
 * - **A `Serial` identity always**: a serial is one unit by definition.
 * - **A `Lot / Serial` identity only on a line of exactly 1**: the posting heading carries lots too, and merging
 *   two lots would hide how much of each went out. A lot line stays its own line.
 * - **A `Lot` identity never** here; put-away's lots are §5's, in `groupByBin`.
 *
 * Merging keys on every other column the line draws — item, description, bin, MRS, unit, disposition — so two
 * MRS or two dispositions in one bin stay two lines. An unparsable figure in a merging group aborts the whole
 * block's merge (null), the same rule as §5: ungrouped truth beats a wrong total.
 */
function groupByIdentity(
  columns: readonly string[],
  rows: readonly (string | null)[][],
  layout: LineLayout,
  factLayout: LineLayout,
  idIdx: number,
  direction: 'in' | 'out' | null,
  inline: boolean
): LinesView | null {
  const idHeading = headingAt(columns, idIdx);
  if (idHeading === 'lot') return null;

  const timeIdx = layout.factIndexes.find((i) => isTimeHeading(headingAt(columns, i)));
  const sumIdxs = layout.factIndexes.filter((i) => SUMMED_HEADINGS.has(headingAt(columns, i)));
  const qtyIdx = sumIdxs.find((i) => QUANTITY_HEADINGS.has(headingAt(columns, i)));
  // A chip-bearing line draws neither its identity (the chip) nor its time (on the chip).
  const chipLayout: LineLayout =
    timeIdx === undefined ? factLayout : { ...factLayout, factIndexes: factLayout.factIndexes.filter((i) => i !== timeIdx) };
  const keyIdxs = [
    layout.codeIndex,
    ...(layout.descriptionIndex === null ? [] : [layout.descriptionIndex]),
    ...factLayout.factIndexes.filter((i) => i !== timeIdx && !sumIdxs.includes(i)),
  ];

  const mergeable = (row: readonly (string | null)[]) =>
    isPresent(row[idIdx]) && (idHeading === 'serial' || (qtyIdx !== undefined && parseQuantity(row[qtyIdx]) === 1));

  const chipOf = (row: readonly (string | null)[]): LineChip => {
    const chip = toChip(row[idIdx]!.trim(), idHeading);
    const time = timeIdx === undefined ? null : row[timeIdx];
    return isPresent(time)
      ? { ...chip, at: shortTime(time), atTitle: `${columns[timeIdx!]} ${time.trim()}` }
      : chip;
  };

  const lineOf = (row: readonly (string | null)[], members: readonly (readonly (string | null)[])[]): LinesViewLine => ({
    code: row[layout.codeIndex] ?? null,
    description: layout.descriptionIndex === null ? null : (row[layout.descriptionIndex] ?? null),
    facts: buildFacts(columns, row, members.length > 0 ? chipLayout : factLayout, direction),
    chips: members.map(chipOf),
    inline,
  });

  // Each entry is a merge group, or a line kept as it is (no identity, or a lot), in first-seen order.
  const order: ({ row: readonly (string | null)[] } | (readonly (string | null)[])[])[] = [];
  const byKey = new Map<string, (readonly (string | null)[])[]>();
  for (const row of rows) {
    if (!mergeable(row)) {
      order.push({ row });
      continue;
    }
    const key = JSON.stringify(keyIdxs.map((i) => row[i]?.trim() ?? null));
    const group = byKey.get(key);
    if (group) group.push(row);
    else {
      const created = [row];
      byKey.set(key, created);
      order.push(created);
    }
  }

  const lines: LinesViewLine[] = [];
  let grouped = false;
  for (const entry of order) {
    if (!Array.isArray(entry)) {
      const plain = entry.row;
      lines.push(lineOf(plain, isPresent(plain[idIdx]) ? [plain] : []));
      continue;
    }
    if (entry.length === 1) {
      lines.push(lineOf(entry[0], entry));
      continue;
    }
    const merged = [...entry[0]];
    for (const i of sumIdxs) {
      let total = 0;
      for (const member of entry) {
        const n = parseQuantity(member[i]);
        if (n === null) return null;
        total += n;
      }
      merged[i] = formatQuantity(total);
    }
    lines.push(lineOf(merged, entry));
    grouped = true;
  }

  return { lines, grouped };
}

function groupByBin(
  columns: readonly string[],
  rows: readonly (string | null)[][],
  layout: LineLayout,
  idIdx: number,
  direction: 'in' | 'out' | null
): LinesViewLine[] | null {
  const qtyIdx = layout.factIndexes.find((i) => QUANTITY_HEADINGS.has(headingAt(columns, i)));
  const unitIdx = layout.factIndexes.find((i) => UNIT_HEADINGS.has(headingAt(columns, i)));
  const idHeading = headingAt(columns, idIdx);

  const byBin = new Map<string, { qty: number; unit: string; chips: LineChip[] }>();

  for (const row of rows) {
    const bin = row[layout.codeIndex];
    if (!isPresent(bin)) return null;

    const qty = qtyIdx === undefined ? null : parseQuantity(row[qtyIdx]);
    if (qty === null) return null; // a wrong total is worse than no grouping

    const key = bin.trim();
    const group = byBin.get(key) ?? { qty: 0, unit: '', chips: [] };
    group.qty += qty;
    if (unitIdx !== undefined && isPresent(row[unitIdx])) group.unit = row[unitIdx]!.trim();
    const idValue = row[idIdx];
    if (isPresent(idValue)) group.chips.push(toChip(idValue.trim(), idHeading));
    byBin.set(key, group);
  }

  return [...byBin.entries()].map(([bin, group]): LinesViewLine => {
    const qtyText = group.unit ? `${formatQuantity(group.qty)} ${group.unit}` : formatQuantity(group.qty);
    return {
      code: bin,
      description: null,
      facts: [
        // The summed quantity is the line's single fact, drawn exactly like an ordinary quantity —
        // hidden label, and the block's direction treatment when one is declared.
        { label: 'Qty', value: qtyText, iconKey: null, hideLabel: true, direction, wraps: false },
      ],
      chips: group.chips,
      inline: true,
    };
  });
}

export { PROSE_MIN_LENGTH, isNumeric as isNumericCell };
